import { Inject, Injectable, Injector, OnDestroy, Optional, Provider, Type } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ListService, PagedResultDto, QueryStreamCreatorCallback } from '@abp/ng.core';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { LIST_FILTERS, ListFilters } from './list-filters';

/** Query-param names owned by paging/sorting/search; custom filters may not reuse them. */
const RESERVED_PARAMS = ['page', 'pageSize', 'sort', 'filter'];

/**
 * ABP ListService whose state (page, pageSize, sort, filter + optional custom
 * filters) is mirrored to the route's query params, so deep links, browser
 * back/forward and "return from edit" restore the exact list state.
 *
 * Custom filters are provided per page via LIST_FILTERS (see ListFilters).
 */
@Injectable()
export class RouteListService extends ListService implements OnDestroy {
  private destroyed$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private injector: Injector,
    @Optional() @Inject(LIST_FILTERS) private filters: ListFilters<any> | null,
  ) {
    super(injector);
    this.assertNoReservedKeys();
    this.watchUrl();
    this.writeToUrl();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    super.ngOnDestroy();
  }

  // ───────────────────────── URL -> ListService ─────────────────────────

  private watchUrl(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe(qp => this.applyFromUrl(qp));
  }

  /** initial load + browser back/forward */
  private applyFromUrl(qp: ParamMap): void {
    // hydrate ABP `filter` + custom filters silently (no request yet)
    const filtersChanged = this.applyFiltersFromUrl(qp);

    const page = Math.max(0, (+qp.get('page') || 1) - 1); // URL is 1-based
    const size = +qp.get('pageSize');
    const sort = qp.get('sort');
    let triggered = false; // any setter below already fires get() (debounced into one request)

    if (size > 0 && size !== this.maxResultCount) {
      this.maxResultCount = size;
      triggered = true;
    }
    if (sort) {
      const [key, order] = sort.split(' ');
      if (key !== this.sortKey) {
        this.sortKey = key;
        triggered = true;
      }
      if ((order || 'asc') !== this.sortOrder) {
        this.sortOrder = order || 'asc';
        triggered = true;
      }
    }
    if (page !== this.page) {
      this.page = page;
      triggered = true;
    }

    // only filters changed (e.g. browser Back between two filter states): re-query
    if (filtersChanged && !triggered) this.getWithoutPageReset();
  }

  private applyFiltersFromUrl(qp: ParamMap): boolean {
    let changed = false;
    const f = qp.get('filter') ?? '';
    if (f !== (this.filter ?? '')) {
      // ABP 7 ListService keeps `filter` in private `_filter`; the public setter would call get()
      (this as unknown as { _filter: string })._filter = f;
      changed = true;
    }
    if (this.filters) changed = this.filters.applyFrom(qp) || changed;
    return changed;
  }

  // ───────────────────────── ListService -> URL ─────────────────────────

  private writeToUrl(): void {
    this.query$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      const queryParams: Record<string, string | number | null> = {
        page: this.page > 0 ? this.page + 1 : null, // null removes the param
        pageSize: this.maxResultCount !== 10 ? this.maxResultCount : null,
        sort: this.sortKey ? `${this.sortKey} ${this.sortOrder}` : null,
        filter: this.filter || null,
        ...(this.filters?.serialize() ?? {}),
      };

      const current = this.route.snapshot.queryParamMap;
      const changed = Object.keys(queryParams).some(
        k => String(queryParams[k] ?? '') !== (current.get(k) ?? ''),
      );
      if (!changed) return; // breaks the URL<->service feedback loop

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
        // replaceUrl: true,  // optional: keep filter keystrokes out of browser history
      });
    });
  }

  // ───────────────────────── request pipeline ─────────────────────────

  override hookToQuery<T>(
    streamCreatorCallback: QueryStreamCreatorCallback<T, any>,
  ): Observable<PagedResultDto<T>> {
    return super.hookToQuery(streamCreatorCallback).pipe(
      tap(response => {
        this.checkOverflow(response.totalCount);
      }),
    );
  }

  private checkOverflow(itemcount?: number) {
    if (!itemcount && this.page == 0) return;

    const maxPages = Math.ceil(itemcount / this.maxResultCount);

    if (!maxPages || this.page + 1 > maxPages) {
      this.router.navigate(['404']);
    }
  }

  // ───────────────────────── CRUD refresh helpers ─────────────────────────

  refreshToFirstPage() {
    if (this.page !== 0) {
      this.page = 0;
    } else {
      this.get();
    }
  }

  refreshCurrentPage() {
    this.getWithoutPageReset();
  }

  refreshAfterDelete(prevTotalCount: number) {
    const newTotal = prevTotalCount - 1;
    const firstIndexOnPage = this.page * this.maxResultCount;
    if (this.page > 0 && firstIndexOnPage >= newTotal) {
      this.page = this.page - 1;
    } else {
      this.getWithoutPageReset();
    }
  }

  saved = (isUpdate: boolean) => () => {
    isUpdate ? this.getWithoutPageReset() : this.get();
  };

  deleted = (prevTotalCount: number) => () => {
    this.refreshAfterDelete(prevTotalCount);
  };

  // ───────────────────────── guards ─────────────────────────

  private assertNoReservedKeys() {
    const clash = this.filters?.keys.find(k => RESERVED_PARAMS.includes(k));
    if (clash) throw new Error(`RouteListService: filter key "${clash}" is reserved for list paging`);
  }
}

/** Pages with only ABP's `filter` search box (page/pageSize/sort/filter persisted). */
export const ROUTE_LIST_PROVIDERS: Provider[] = [
  { provide: ListService, useClass: RouteListService },
  { provide: RouteListService, useExisting: ListService },
];

/** Pages with structured custom filters: pass their ListFilters subclass. */
export function provideRouteList(filters?: Type<ListFilters<any>>): Provider[] {
  return [
    ...ROUTE_LIST_PROVIDERS,
    ...(filters
      ? [
          { provide: LIST_FILTERS, useClass: filters },
          { provide: filters, useExisting: LIST_FILTERS },
        ]
      : []),
  ];
}
